import { Component, Input, EventEmitter, Output, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { UserService } from '../../core/services/data/user.service';
import { OrderService } from '../../core/services/data/order.service';
import { IUser } from '../../types/datatypes';

type PurchaseInfo = {
	email: string;
	firstName: string;
	lastName: string;
};

@Component({
	selector: 'app-signup-login',
	templateUrl: './signup-login.component.html',
	styleUrls: ['./signup-login.component.scss'],

})
export class SignupLoginComponent implements OnInit {
	@Input() existingUser = false;

	@Input() set purchaserInfo(info: PurchaseInfo) {
		this.signupForm.get('email').setValue(info.email);
		this.signupForm.get('firstName').setValue(info.firstName);
		this.signupForm.get('lastName').setValue(info.lastName);
	}

	@Output() public user: EventEmitter<IUser> = new EventEmitter();

	public progressMessage = 'Processing.';

	public state = 'display';

	public error = '';

	public isNewUser = false;

	public progressInterval = null;

	public signupError: boolean | string = false;

	public signupForm = new FormGroup({
		email: new FormControl('', [Validators.email, Validators.required]),
		password: new FormControl('', [Validators.minLength(8), Validators.required]),
		firstName: new FormControl(''),
		lastName: new FormControl(''),
	});

	public _user = null;

	public loginForm = new FormGroup({
		email: new FormControl('', [Validators.email, Validators.required]),
		password: new FormControl('', [Validators.required]),
	});

	public mode: 'signup' | 'login' = 'signup';

	public show = false;

	public subscription;

	constructor(
		private userService: UserService,
		private orderService: OrderService,
	) {

	}

	ngOnInit(): void {
		this.isNewUser = false;

		this.userService.author$.subscribe((user) => {
			this._user = user;
		});

		this.show = !this.existingUser;

		this.orderService.orderStore$.subscribe((order) => {
			this.subscription = this.orderService.getSubscriptionFromOrder();
		});
	}

	toggleMode() {
		this.mode = this.mode === 'signup' ? 'login' : 'signup';
	}

	signup() {
		if (this.signupForm.valid) {
			this.signupError = false;
			this.create();
			return;
		}

		this.signupError = this.handleSignupErrors(this.signupForm.get('password').errors);
	}

	create() {
		if (this.signupForm.invalid) {
			return;
		}
		this.processing();
		this.isNewUser = true;
		const email = this.signupForm.get('email').value;
		const password = this.signupForm.get('password').value;

		this.userService.createAndLogin({
			_id: this.subscription?.item?.user,
			username: email,
			password,
			firstName: this.signupForm.get('firstName').value,
			lastName: this.signupForm.get('lastName').value,
		}).subscribe((result: any) => {
			if (result.success) {
				this.cancelProcessing();
				this.login(email, password, true);
			} else {
				this.state = 'error';
				this.error = result.message ? result.message : 'An error occurred, please try again.';
				this.revertToSignupLogin();
			}
		});
	}

	login(username = this.loginForm.get('email').value, password = this.loginForm.get('password').value, override = false) {
		if (this.loginForm.invalid && !override) {
			return;
		}

		this.userService.login(
			username,
			password,
		).subscribe((result: any) => {
			if (result.success) {
				this.userService.updateAuthor(result.user);
				this.userService.updateUser(result.user);
				this.user.emit(result.user);
				this.state = 'finished';

				// Update Order with User
				this.orderService.addUserToOrder(result.user._id).subscribe();
			} else {
				this.state = 'error';
				this.error = result.message ? result.message : 'An error occurred, please try again.';
				this.revertToSignupLogin();
			}
		});
	}

	confirmError() {
		this.state = 'display';
		this.error = '';
	}

	revertToSignupLogin() {
		setTimeout(() => {
			this.state = 'display';
			this.error = '';
		}, 5000);
	}

	handleSignupErrors(errors: any) {
		if (errors.minlength) {
			return 'Password must be at least 8 characters long';
		}

		return false;
	}

	processing() {
		this.state = 'inProgress';
		this.progressInterval = setInterval(() => {
			if (this.progressMessage === 'Processing.') {
				this.progressMessage = 'Processing..';
				return;
			}

			if (this.progressMessage === 'Processing..') {
				this.progressMessage = 'Processing...';
				return;
			}

			if (this.progressMessage === 'Processing...') {
				this.progressMessage = 'Processing.';
			}
		}, 500);
	}

	cancelProcessing() {
		clearInterval(this.progressInterval);
	}
}
